import { ApolloCache, NetworkStatus, Reference } from '@apollo/client'
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common'
import React, { useCallback, useMemo } from 'react'

import { ApolloModifierDetails, wrapQueryModifier } from '~apollo/helpers'
import { launchObjTypename, productBoardItemsGroupTypename, productBoardReleaseTypename } from '~apollo/typenames'
import { useProductBoardItemsGroupsQuery } from '~generated/hooks'
import {
  CustomFieldSortOptionFragment,
  FieldSortOptionFragment,
  PaginatedProductBoardItemsQueryVariables,
  ProductBoardItemFactorFragment,
  ProductBoardItemsGroupDetailsFragment,
  ProductBoardItemsGroupsQueryVariables,
  ProductBoardItemTableRowDataFragment,
  ProductBoardItemTimelinePreviewFragment,
  ProductBoardRoadmapWithItemsFragment,
  RoadmapItemCustomColumnFragment,
} from '~generated/operations'
import {
  CreateProductBoardItemInput,
  PaginatedProductBoardItemsObj,
  PaginatedRoadmapItemsGroupsObj,
  ProductBoardItemsFilterInput,
  ProductBoardItemsGroupBy,
  ProductBoardItemsGroupObj,
  ProductBoardItemsSortInput,
  SortOrder,
} from '~generated/types'
import BattleCardsIcon from '~icons/battleCards.svg'
import DealIcon from '~icons/deal.svg'
import DotIcon from '~icons/dot.svg'
import ReleaseIcon from '~icons/flag.svg'
import LaunchRocketIcon from '~icons/launchRocket.svg'
import RoadmapIcon from '~icons/roadmap.svg'
import StatusSmallIcon from '~icons/statuses/status.small.svg'
import Avatar from '~lib/ui-kit/Avatar/Avatar'
import { filterProductBoardItems } from '~modules/productRoadmapItems/usecases/useFilterProductBoardItems'
import { defaultPaginatedRoadmapItemsPagination } from '~modules/productRoadmapItems/usecases/usePaginatedRoadmapItems'
import InsightIcon from '~shared/icons/20/insight.svg'

/**
 * Hook to persist active roadmap by saving last opened roadmap in local storage
 */

type GroupInCache = { __typename?: 'ProductBoardItemsGroupObj' } & Pick<ProductBoardItemsGroupObj, 'id'> &
  Omit<ProductBoardItemsGroupDetailsFragment, 'items'> & { items: Reference[] }

export type RoadmapGroup = {
  id?: string | null // id of another entity
  key: ProductBoardItemsGroupBy
  name: string
  imageSrc?: string
  iconColor?: string
  itemsFilter: ProductBoardItemsFilterInput
  items: ProductBoardItemTimelinePreviewFragment[]
}

type GroupOptions = {
  fieldKey?: keyof CreateProductBoardItemInput
  filterFieldKey: keyof ProductBoardItemsFilterInput
  getGroupIcon: (group: ProductBoardItemsGroupDetailsFragment) => React.ReactNode // TODO Move to ui component
  getItemGroups: (item: ProductBoardItemTableRowDataFragment) => Omit<RoadmapGroup, 'itemsFilter' | 'items'>[] | null
  sortGroups: (groups: GroupInCache[], details: ApolloModifierDetails) => GroupInCache[]
  isArray: boolean
}

type SortGroupType<T> = (Omit<GroupInCache, 'key'> & { key: T })[]

export const roadmapItemsGroupsOptions: Record<ProductBoardItemsGroupBy, GroupOptions> = {
  [ProductBoardItemsGroupBy.ROADMAP]: {
    fieldKey: 'roadmapId',
    filterFieldKey: 'roadmapIds',
    isArray: false,
    getGroupIcon: (_group) => <RoadmapIcon />,
    getItemGroups: (item) => [{ id: item.roadmap.id, name: '', key: ProductBoardItemsGroupBy.ROADMAP }], // TODO
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.ROADMAP>) => {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  [ProductBoardItemsGroupBy.RELEASE]: {
    fieldKey: 'releaseId',
    filterFieldKey: 'releaseIds',
    isArray: false,
    getGroupIcon: (_group) => <ReleaseIcon />,
    getItemGroups: (item) =>
      item.release
        ? [{ id: item.release.id, name: item.release.name, key: ProductBoardItemsGroupBy.RELEASE, icon: null }]
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.RELEASE>, { readField }) => {
      // TODO use union type for entity instead of storing name imageSrc and color in common group obj
      const endDateByGroupId = new Map(
        groups.map((group) => [group.id, readField('endDate', { __ref: `${productBoardReleaseTypename}:${group.id}` })])
      )

      return [...groups].sort((a, b) => {
        const dateA = endDateByGroupId.get(a.id)
        const dateB = endDateByGroupId.get(b.id)

        return dateA < dateB ? -1 : dateA > dateB ? 1 : a.name.localeCompare(b.name)
      })
    },
  },
  [ProductBoardItemsGroupBy.ASSIGNEE]: {
    fieldKey: 'assigneeId',
    filterFieldKey: 'assigneeIds',
    isArray: false,
    getGroupIcon: ({ imageSrc, name }) => <Avatar src={imageSrc} alt={name} size="xs" />,
    getItemGroups: (item) =>
      item.assignee
        ? [
            {
              id: item.assignee.id,
              name: item.assignee.name || item.assignee.email,
              key: ProductBoardItemsGroupBy.ASSIGNEE,
              imageSrc: item.assignee.profileImage,
            },
          ]
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.ASSIGNEE>) => {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  [ProductBoardItemsGroupBy.STATUS]: {
    fieldKey: 'statusId',
    filterFieldKey: 'statusIds',
    isArray: false,
    getGroupIcon: ({ iconColor }) => <StatusSmallIcon color={iconColor} />,
    getItemGroups: (item) =>
      item.status
        ? [
            {
              id: item.status.id,
              name: item.status.name,
              key: ProductBoardItemsGroupBy.STATUS,
              iconColor: item.status.iconColor,
            },
          ]
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.RELEASE>) => {
      const PRODUCT_BOARD_DEFAULT_STATUSES_SORT_ORDER = {
        'To do': 0,
        'In progress': 1,
        Done: 2,
        Blocked: 3,
        Delayed: 4,
        CUSTOM: 5,
      }

      return [...groups].sort((a, b) => {
        // The default statuses should come first and follow the order: To do -> In progress -> Done -> Blocked -> Delayed
        const sortOrderA =
          PRODUCT_BOARD_DEFAULT_STATUSES_SORT_ORDER[a.name] || PRODUCT_BOARD_DEFAULT_STATUSES_SORT_ORDER.CUSTOM
        const sortOrderB =
          PRODUCT_BOARD_DEFAULT_STATUSES_SORT_ORDER[b.name] || PRODUCT_BOARD_DEFAULT_STATUSES_SORT_ORDER.CUSTOM

        if (sortOrderA === sortOrderB) {
          // custom status
          return a.name.localeCompare(b.name)
        }

        return sortOrderA - sortOrderB
        a.name.localeCompare(b.name)
      })
    },
  },
  [ProductBoardItemsGroupBy.TAG]: {
    fieldKey: 'tagIds',
    filterFieldKey: 'tagIds',
    isArray: true,
    getGroupIcon: ({ iconColor }) => <DotIcon fill={iconColor} />,
    getItemGroups: (item) =>
      item.tags?.length > 0
        ? item.tags.map((tag) => ({
            id: tag.id,
            name: tag.label,
            key: ProductBoardItemsGroupBy.TAG,
            iconColor: tag.color,
          }))
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.ASSIGNEE>) => {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  [ProductBoardItemsGroupBy.LAUNCH]: {
    fieldKey: 'launchIds', // TODO
    filterFieldKey: 'launchIds',
    isArray: true,
    getGroupIcon: (_group) => <LaunchRocketIcon />,
    getItemGroups: (item) =>
      item.launches?.length > 0
        ? item.launches.map((launch) => ({ id: launch.id, name: launch.name, key: ProductBoardItemsGroupBy.LAUNCH }))
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.LAUNCH>, { readField }) => {
      const launchDateByGroupId = new Map(
        groups.map((group) => [group.id, readField('launchDate', { __ref: `${launchObjTypename}:${group.id}` })])
      )

      return [...groups].sort((a, b) => {
        const dateA = launchDateByGroupId.get(a.id)
        const dateB = launchDateByGroupId.get(b.id)

        return dateA < dateB ? -1 : dateA > dateB ? 1 : a.name.localeCompare(b.name)
      })
    },
  },
  [ProductBoardItemsGroupBy.DEAL]: {
    fieldKey: 'dealIds',
    filterFieldKey: 'dealIds',
    isArray: true,
    getGroupIcon: (_group) => <DealIcon />,
    getItemGroups: (item) =>
      item.deals?.length > 0
        ? item.deals.map((deal) => ({ id: deal.id, name: deal.name, key: ProductBoardItemsGroupBy.DEAL }))
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.DEAL>) => {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  [ProductBoardItemsGroupBy.PERSONA]: {
    fieldKey: 'personaIds',
    filterFieldKey: 'personaIds',
    isArray: true,
    getGroupIcon: ({ imageSrc, name }) => <Avatar src={imageSrc} alt={name} size="xs" />,
    getItemGroups: (item) =>
      item.personas?.length > 0
        ? item.personas.map((persona) => ({
            id: persona.id,
            name: persona.name,
            key: ProductBoardItemsGroupBy.PERSONA,
          }))
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.PERSONA>) => {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  [ProductBoardItemsGroupBy.COMPETITOR]: {
    fieldKey: 'competitorIds',
    filterFieldKey: 'competitorIds',
    isArray: true,
    getGroupIcon: ({ imageSrc, name }) => <Avatar src={imageSrc} alt={name} size="xs" />,
    getItemGroups: (item) =>
      item.competitors?.length > 0
        ? item.competitors.map((comp) => ({ id: comp.id, name: comp.name, key: ProductBoardItemsGroupBy.COMPETITOR }))
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.COMPETITOR>) => {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  [ProductBoardItemsGroupBy.PRODUCT_IDEA]: {
    fieldKey: 'feedbackPostIds',
    filterFieldKey: 'feedbackPostIds',
    isArray: true,
    getGroupIcon: (_) => <BattleCardsIcon />,
    getItemGroups: (item) =>
      item.feedbackPosts?.length > 0
        ? item.feedbackPosts.map((post) => ({
            id: post.id,
            name: post.title,
            key: ProductBoardItemsGroupBy.PRODUCT_IDEA,
          }))
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.PRODUCT_IDEA>) => {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  [ProductBoardItemsGroupBy.INSIGHT]: {
    fieldKey: 'insightIds',
    filterFieldKey: 'insightIds',
    isArray: true,
    getGroupIcon: (_) => <InsightIcon />,
    getItemGroups: (item) =>
      item.insights?.length > 0
        ? item.insights.map((insight) => ({
            id: insight.id,
            name: insight.name,
            key: ProductBoardItemsGroupBy.INSIGHT,
          }))
        : null,
    sortGroups: (groups: SortGroupType<ProductBoardItemsGroupBy.INSIGHT>) => {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
  },
}

type UseRoadmapItemsGroupsProps = {
  groupBy: ProductBoardItemsGroupBy
  filter: ProductBoardItemsFilterInput
  viewId: string | null
}

const ROADMAP_ITEMS_GROUPS_LIMIT = 5
export const defaultPaginatedRoadmapGroupsPagination = { limit: ROADMAP_ITEMS_GROUPS_LIMIT, page: 1 }

export default function useRoadmapItemsGroups({ viewId, groupBy, filter }: UseRoadmapItemsGroupsProps) {
  const {
    networkStatus,
    data: { productBoardItemsGroups } = {},
    loading,
    fetchMore,
  } = useProductBoardItemsGroupsQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    // nextFetchPolicy: 'cache-first',
    variables: {
      viewId,
      groupBy,
      filter: {
        ...filter,
        parentItemId: null,
      },
      pagination: defaultPaginatedRoadmapGroupsPagination,
    },
  })

  const groups = useMemo<RoadmapGroup[]>(() => {
    if (!productBoardItemsGroups?.docs) return []

    return productBoardItemsGroups.docs.map((group) => {
      const { filterFieldKey } = roadmapItemsGroupsOptions[group.key]

      const isOtherGroup = group.id === null

      return {
        ...group,
        itemsFilter: {
          ...filter,
          [filterFieldKey]: isOtherGroup ? filter[filterFieldKey] || null : [group.id],
          parentItemId: null,
        },
      }
    })
  }, [filter, productBoardItemsGroups?.docs])

  const loadMoreGroups = useCallback(() => {
    if (
      networkStatus === NetworkStatus.ready &&
      productBoardItemsGroups &&
      productBoardItemsGroups.totalPages > productBoardItemsGroups.page
    ) {
      fetchMore({
        variables: {
          pagination: {
            page: productBoardItemsGroups.page + 1,
            limit: ROADMAP_ITEMS_GROUPS_LIMIT,
          },
        },
      })
    }
  }, [networkStatus, productBoardItemsGroups, fetchMore])

  return {
    networkStatus,
    groups,
    loading,
    loadMoreGroups,
    loadMoreLoading: networkStatus === NetworkStatus.fetchMore,
  }
}

function sortProductBoardItemsReferences({
  items = [],
  sort,
  readField,
}: {
  items: Reference[]
  sort: ProductBoardItemsSortInput
  readField: ReadFieldFunction
}) {
  const sortComparers: Record<
    keyof Omit<ProductBoardItemsSortInput, 'customColumn' | 'factor'>,
    (a: Reference, b: Reference) => number
  > = {
    revenueImpact: (a, b) => {
      const aRevenueImpact = readField<number>('revenueImpact', a)
      const bRevenueImpact = readField<number>('revenueImpact', b)
      if (aRevenueImpact === bRevenueImpact) return 0
      if (!aRevenueImpact) return -1
      if (!bRevenueImpact) return 1

      return aRevenueImpact - bRevenueImpact
    },
    priority: (a, b) => {
      const aPriority = readField<string>('priority', a)
      const bPriority = readField<string>('priority', b)
      if (aPriority === bPriority) return 0

      if (!aPriority) return -1
      if (!bPriority) return 1
      return aPriority.localeCompare(bPriority)
    },
    assignee: (a, b) => {
      const nestedRefA = readField<Reference>('assignee', a)
      const nestedRefB = readField<Reference>('assignee', b)
      if (!nestedRefA && !nestedRefB) return 0
      if (!nestedRefA) return -1
      if (!nestedRefB) return 1
      return readField<string>('email', nestedRefA).localeCompare(readField<string>('email', nestedRefB))
    },
    createdAt: (a, b) => {
      // TODO use createdAt
      const aDate = readField<string>('startDate', a) || null
      const bDate = readField<string>('startDate', b) || null
      if (aDate === bDate) return 0

      if (!aDate) return -1
      if (!bDate) return 1
      // TODO use timestamp instead of ISO string
      return new Date(aDate).getTime() > new Date(bDate).getTime() ? 1 : -1
    },
    effort: (a, b) => {
      const aEffort = readField<number>('effort', a) || null
      const bEffort = readField<number>('effort', b) || null
      if (aEffort === bEffort) return 0

      if (!aEffort) return -1
      if (!bEffort) return 1
      return aEffort - bEffort
    },
    endDate: (a, b) => {
      const aDate = readField<string>('endDate', a)
      const bDate = readField<string>('endDate', b)
      if (aDate === bDate) return 0

      if (!aDate) return -1
      if (!bDate) return 1
      // TODO use timestamp instead of ISO string
      return new Date(aDate).getTime() > new Date(bDate).getTime() ? 1 : -1
    },
    orderIndex: (a, b) => {
      const aIndex = readField<string>('orderIndex', a)
      const bIndex = readField<string>('orderIndex', b)
      // FIXME multiple items can have the same order index for some reason
      if (aIndex === bIndex) return 0
      return aIndex > bIndex ? 1 : -1
    },
    release: (a, b) => {
      const nestedRefA = readField<Reference>('release', a)
      const nestedRefB = readField<Reference>('release', b)
      if (!nestedRefA && !nestedRefB) return 0
      if (!nestedRefA) return -1
      if (!nestedRefB) return 1
      return readField<string>('name', nestedRefA).localeCompare(readField('name', nestedRefB))
    },
    roadmap: (a, b) => {
      const nestedRefA = readField<Reference>('roadmap', a)
      const nestedRefB = readField<Reference>('roadmap', b)
      if (!nestedRefA && !nestedRefB) return 0
      if (!nestedRefA) return -1
      if (!nestedRefB) return 1
      return readField<string>('name', nestedRefA).localeCompare(readField('name', nestedRefB))
    },
    startDate: (a, b) => {
      const aDate = readField<string>('startDate', a) || null
      const bDate = readField<string>('startDate', b) || null
      if (aDate === bDate) return 0

      if (!aDate) return -1
      if (!bDate) return 1
      // TODO use timestamp instead of ISO string
      return new Date(aDate).getTime() - new Date(bDate).getTime()
    },
    status: (a, b) => {
      const nestedRefA = readField<Reference>('status', a)
      const nestedRefB = readField<Reference>('status', b)
      if (!nestedRefA && !nestedRefB) return 0
      if (!nestedRefA) return -1
      if (!nestedRefB) return 1
      return readField<string>('name', nestedRefA).localeCompare(readField('name', nestedRefB))
    },
    title: (a, b) => {
      const aTitle = readField<string>('title', a)
      const bTitle = readField<string>('title', b)

      return aTitle.localeCompare(bTitle)
    },
    score: (a, b) => {
      const aScore = readField<number>('score', a) || null
      const bScore = readField<number>('score', b) || null
      if (aScore === bScore) return 0
      if (!aScore) return -1
      if (!bScore) return 1

      return aScore - bScore
    },
    value: (a, b) => {
      const aImmutableFactor = readField<ProductBoardItemFactorFragment[]>('factors', a).find((f) => f.immutable)
      const bImmutableFactor = readField<ProductBoardItemFactorFragment[]>('factors', b).find((f) => f.immutable)

      const aValue = aImmutableFactor?.value || 0
      const bValue = bImmutableFactor?.value || 0
      if (aValue === bValue) return 0

      if (!aValue) return -1
      if (!bValue) return 1
      return aValue - bValue
    },
  }

  const key = ((sort && [...Object.keys(sort)].sort((a, b) => a.localeCompare(b))[0]) ||
    'orderIndex') as keyof ProductBoardItemsSortInput

  const { order } = (sort?.[key] as FieldSortOptionFragment) || { order: SortOrder.ASC }

  if (key === 'orderIndex') {
    return items.slice().sort((a, b) => {
      return order === SortOrder.DESC ? sortComparers.orderIndex(a, b) : sortComparers.orderIndex(b, a)
    })
  }

  if (['customColumn', 'factor'].includes(key)) {
    const { customFieldId } = sort[key] as CustomFieldSortOptionFragment

    return [...items].sort((a, b) => {
      const arrKey = `${key}s` as 'customColumns' | 'factors'
      const fieldIdKey = `${key}Id` as 'customColumnId' | 'factorId'

      const firstCustomFieldValue =
        readField<(RoadmapItemCustomColumnFragment | ProductBoardItemFactorFragment)[]>(arrKey, a).find(
          (itemColumnOrFactor) => readField(fieldIdKey, itemColumnOrFactor) === customFieldId
        )?.value || null

      const secondCustomFieldValue =
        readField<(RoadmapItemCustomColumnFragment | ProductBoardItemFactorFragment)[]>(arrKey, b).find(
          (itemColumnOrFactor) => readField(fieldIdKey, itemColumnOrFactor) === customFieldId
        )?.value || null

      if (firstCustomFieldValue === secondCustomFieldValue) return 0

      if (!firstCustomFieldValue) return -1
      if (!secondCustomFieldValue) return 1

      return (firstCustomFieldValue > secondCustomFieldValue ? 1 : -1) * (order === SortOrder.ASC ? 1 : -1)
    })
  }

  const compareFn = sortComparers[key]

  return [...items].sort((a, b) => compareFn(a, b) * (order === SortOrder.ASC ? 1 : -1))
}

export function modifyRoadmapItemsConnection(options: {
  connectionFilter?: ProductBoardItemsFilterInput
  addItems?: ProductBoardItemTableRowDataFragment[]
  deleteItems?: ProductBoardItemTableRowDataFragment[]
}) {
  if (!options.addItems?.length && !options.deleteItems?.length) {
    return (prev) => prev
  }

  // TODO deleteItems
  const itemsIds = new Set(Array.isArray(options.addItems) ? options.addItems.map((item) => item.id) : [])

  return wrapQueryModifier<
    Omit<PaginatedProductBoardItemsObj, 'docs'> & { docs: Reference[] },
    PaginatedProductBoardItemsQueryVariables
  >((prev, details) => {
    const { variables, readField, toReference } = details

    const filter = { ...variables?.filter, ...options.connectionFilter }
    const sort = variables?.sort || { orderIndex: { order: SortOrder.ASC } }

    const limit = prev.limit || defaultPaginatedRoadmapItemsPagination.limit

    const filteredItems = filterProductBoardItems({ items: options.addItems, filter })

    if (filteredItems.length === 0) {
      if (!prev.docs.some((ref) => itemsIds.has(readField('id', ref)))) {
        return prev
      }

      return {
        ...prev,
        docs: prev.docs.filter((ref) => !itemsIds.has(readField('id', ref))),
        page: Math.floor((prev.docs.length - 1) / prev.limit) || 1,
        totalPages: prev.docs.length === prev.totalPages * limit ? prev.totalPages - 1 : prev.totalPages,
      }
    }

    const newDocs = sortProductBoardItemsReferences({
      items: [
        ...filteredItems.map((updatedItem) => toReference(updatedItem)),
        ...prev.docs.filter((ref) => !itemsIds.has(readField('id', ref))),
      ],
      sort,
      readField,
    }).slice(0, prev.page * limit)

    return {
      ...prev,
      docs: newDocs,
      page: Math.floor(newDocs.length / limit) || 1,
      totalPages: newDocs.length > prev.totalPages * limit ? prev.totalPages + 1 : prev.totalPages,
    }
  })
}

// export function modifyRoadmapItemsGroupsAfterItemsBulkUpdate(
//   cache: ApolloCache<any>,
//   items: ProductBoardItemTableRowDataFragment[]
// ) {
//   const itemsIds = new Set(items.map((item) => item.id))

//   return cache.modify({
//     fields: {
//       productBoardItemsGroups: wrapQueryModifier<
//         { __typename?: 'PaginatedRoadmapItemsGroupsObj' } & Pick<
//           PaginatedRoadmapItemsGroupsObj,
//           'limit' | 'page' | 'totalPages'
//         > & {
//             docs: Array<GroupInCache>
//           },
//         ProductBoardItemsGroupsQueryVariables
//       >((prev, details) => {
//         const { variables, toReference, readField } = details

//         const filteredItems = filterProductBoardItems({ items, filter: variables.filter })
//         if (filteredItems.length === 0) return prev

//         const cachedGroups = prev.docs // group is a obj, not a ref

//         const { getItemGroups, sortGroups } = roadmapItemsGroupsOptions[variables.groupBy]

//         const newGroupsById = new Map()
//         const updatedGroupItemsByGroupId = new Map()

//         filteredItems.forEach((item) => {
//           const itemGroups = getItemGroups(item) || [{ id: null, name: 'Other', key: variables.groupBy }]
//           itemGroups.forEach((groupInfo) => {
//             const groupInCache = cachedGroups.find((g) => g.id === groupInfo.id)

//             if (groupInCache) {
//               const existingItems =
//                 updatedGroupItemsByGroupId.get(groupInCache.id) ||
//                 groupInCache.items.filter((ref) => !itemsIds.has(readField('id', ref)))

//               updatedGroupItemsByGroupId.set(groupInCache.id, [...existingItems, toReference(item)]) // we will sort them on next step
//               return
//             }

//             newGroupsById.set(groupInfo.id, {
//               ...groupInfo,
//               __typename: productBoardItemsGroupTypename,
//               items: [...(newGroupsById.get(groupInfo.id) || []), toReference(item)], // we will sort them on next step
//             })
//           })
//         })

//         const sortedGroups = sortGroups(
//           [
//             ...cachedGroups.map((groupInCache) => {
//               if (updatedGroupItemsByGroupId.has(groupInCache.id)) {
//                 return {
//                   ...groupInCache,
//                   items: sortProductBoardItemsReferences({
//                     items: updatedGroupItemsByGroupId.get(groupInCache.id),
//                     sort: { orderIndex: { order: SortOrder.ASC } },
//                     readField,
//                   }),
//                 }
//               }

//               const shouldRemoveItemsFromGroup = groupInCache.items.some((ref) => itemsIds.has(readField('id', ref)))
//               if (shouldRemoveItemsFromGroup) {
//                 return {
//                   ...groupInCache,
//                   items: groupInCache.items.filter((ref) => !itemsIds.has(readField('id', ref))),
//                 }
//               }

//               return groupInCache
//             }),
//             ...Object.values(newGroupsById).map((group) => {
//               return {
//                 ...group,
//                 items: sortProductBoardItemsReferences({
//                   items: group.items,
//                   sort: { orderIndex: { order: SortOrder.ASC } },
//                   readField,
//                 }),
//               }
//             }),
//           ],
//           details
//         )

//         const limit = prev.limit || ROADMAP_ITEMS_GROUPS_LIMIT

//         const newDocs = sortedGroups.slice(0, prev.page * limit)

//         return {
//           ...prev,
//           docs: newDocs,
//           page: Math.floor(newDocs.length / limit) || 1,
//           totalPages: sortedGroups.length > prev.totalPages * limit ? prev.totalPages + 1 : prev.totalPages,
//         }
//       }),
//       // paginatedProductBoardItems: wrapQueryModifier<
//       //   Omit<PaginatedProductBoardItemsObj, 'docs'> & { docs: Reference[] },
//       //   PaginatedProductBoardItemsQueryVariables
//       // >((prev, details) => {
//       //   const { variables, readField, toReference } = details

//       //   const limit = prev.limit || defaultPaginatedRoadmapItemsPagination.limit

//       //   const filteredItems = filterProductBoardItems({ items, filter: variables.filter })

//       //   if (filteredItems.length === 0) {
//       //     if (!prev.docs.some((ref) => itemsIds.has(readField('id', ref)))) {
//       //       return prev
//       //     }

//       //     return {
//       //       ...prev,
//       //       docs: prev.docs.filter((ref) => !itemsIds.has(readField('id', ref))),
//       //       page: Math.floor((prev.docs.length - 1) / prev.limit) || 1,
//       //       totalPages: prev.docs.length === prev.totalPages * limit ? prev.totalPages - 1 : prev.totalPages,
//       //     }
//       //   }

//       //   const newDocs = sortProductBoardItemsReferences({
//       //     items: [
//       //       ...filteredItems.map((updatedItem) => toReference(updatedItem)),
//       //       ...prev.docs.filter((ref) => !itemsIds.has(readField('id', ref))),
//       //     ],
//       //     sort: variables.sort,
//       //     readField,
//       //   }).slice(0, prev.page * limit)

//       //   return {
//       //     ...prev,
//       //     docs: newDocs,
//       //     page: Math.floor(newDocs.length / limit) || 1,
//       //     totalPages: newDocs.length > prev.totalPages * limit ? prev.totalPages + 1 : prev.totalPages,
//       //   }
//       // }),
//     },
//   })
// }

export function modifyRoadmapItemsGroupsAfterItemCreation(
  cache: ApolloCache<any>,
  newItem: ProductBoardItemTableRowDataFragment
) {
  return cache.modify({
    fields: {
      productBoardItemsGroups: wrapQueryModifier<
        { __typename?: 'PaginatedRoadmapItemsGroupsObj' } & Pick<
          PaginatedRoadmapItemsGroupsObj,
          'limit' | 'page' | 'totalPages'
        > & {
            docs: Array<
              { __typename?: 'ProductBoardItemsGroupObj' } & Pick<ProductBoardItemsGroupObj, 'id'> &
                Omit<ProductBoardItemsGroupDetailsFragment, 'items'> & { items: Reference[] }
            >
          },
        ProductBoardItemsGroupsQueryVariables
      >((prev, details) => {
        const { variables, toReference } = details
        const limit = prev.limit || ROADMAP_ITEMS_GROUPS_LIMIT

        const itemMatchesFilters = filterProductBoardItems({ items: [newItem], filter: variables.filter }).length === 1
        if (!itemMatchesFilters) return prev

        const cachedGroups = prev.docs // group is a obj, not a ref

        const { getItemGroups, sortGroups } = roadmapItemsGroupsOptions[variables.groupBy]

        const itemGroups = getItemGroups(newItem) || [{ id: null, name: 'Other', key: variables.groupBy }]

        const newItemsGroups = []
        const existingGroupsIds = new Set()

        itemGroups.forEach((itemGroup) => {
          const exists = cachedGroups.some((g) => g.id === itemGroup.id)
          if (exists) {
            existingGroupsIds.add(itemGroup.id)
          } else {
            newItemsGroups.push(itemGroup)
          }
        })

        const sortedGroups = sortGroups(
          [
            ...cachedGroups.map((group) => {
              if (!existingGroupsIds.has(group.id)) return group

              return {
                ...group,
                items: [toReference(newItem), ...group.items],
              }
            }),
            ...newItemsGroups.map((itemGroup) => ({
              ...itemGroup,
              __typename: productBoardItemsGroupTypename,
              items: [toReference(newItem)],
            })),
          ],
          details
        )

        const newDocs = sortedGroups.slice(0, prev.page * limit)

        return {
          ...prev,
          docs: newDocs,
          page: Math.floor(newDocs.length / limit) || 1,
          totalPages: sortedGroups.length > prev.totalPages * limit ? prev.totalPages + 1 : prev.totalPages,
        }
      }),
      paginatedProductBoardItems: wrapQueryModifier<
        Omit<PaginatedProductBoardItemsObj, 'docs'> & { docs: Reference[] },
        PaginatedProductBoardItemsQueryVariables
      >((prev, details) => {
        // const { variables, toReference, readField } = details
        const { variables, toReference } = details

        const limit = prev.limit || defaultPaginatedRoadmapItemsPagination.limit

        const itemMatchesFilters = filterProductBoardItems({ items: [newItem], filter: variables.filter }).length === 1
        if (!itemMatchesFilters) return prev

        // const newDocs = sortProductBoardItemsReferences({
        //   items: [toReference(newItem), ...prev.docs],
        //   sort: variables.sort,
        //   readField,
        // }).slice(0, prev.page * limit)
        const newDocs = [toReference(newItem), ...prev.docs].slice(0, prev.page * limit)
        return {
          ...prev,
          docs: newDocs,
          page: Math.floor(newDocs.length / limit) || 1,
          totalPages:
            newDocs.length > prev.totalPages * defaultPaginatedRoadmapItemsPagination.limit
              ? prev.totalPages + 1
              : prev.totalPages,
        }
      }),
    },
  })
}

export function modifyRoadmapItemsGroupsAfterItemsBulkCreation(
  cache: ApolloCache<any>,
  newItems: ProductBoardItemTableRowDataFragment[]
) {
  return cache.modify({
    fields: {
      productBoardItemsGroups: wrapQueryModifier<
        { __typename?: 'PaginatedRoadmapItemsGroupsObj' } & Pick<
          PaginatedRoadmapItemsGroupsObj,
          'limit' | 'page' | 'totalPages'
        > & {
            docs: Array<GroupInCache>
          },
        ProductBoardItemsGroupsQueryVariables
      >((prev, details) => {
        const { variables, toReference, readField } = details

        const filteredItems = filterProductBoardItems({ items: newItems, filter: variables.filter })
        if (filteredItems.length === 0) return prev

        const cachedGroups = prev.docs // group is a obj, not a ref

        const { getItemGroups, sortGroups } = roadmapItemsGroupsOptions[variables.groupBy]

        const newGroupsById = new Map()
        const updatedGroupItemsByGroupId = new Map()

        filteredItems.forEach((item) => {
          const itemGroups = getItemGroups(item) || [{ id: null, name: 'Other', key: variables.groupBy }]
          itemGroups.forEach((groupInfo) => {
            const groupInCache = cachedGroups.find((g) => g.id === groupInfo.id)

            if (groupInCache) {
              const existingItems = updatedGroupItemsByGroupId.get(groupInCache.id) || groupInCache.items

              updatedGroupItemsByGroupId.set(groupInCache.id, [...existingItems, toReference(item)]) // we will sort them on next step
              return
            }

            newGroupsById.set(groupInfo.id, {
              ...groupInfo,
              __typename: productBoardItemsGroupTypename,
              items: [...(newGroupsById.get(groupInfo.id) || []), toReference(item)], // we will sort them on next step
            })
          })
        })

        const sortedGroups = sortGroups(
          [
            ...cachedGroups.map((groupInCache) => {
              if (updatedGroupItemsByGroupId.has(groupInCache.id)) {
                return {
                  ...groupInCache,
                  items: sortProductBoardItemsReferences({
                    items: updatedGroupItemsByGroupId.get(groupInCache.id),
                    sort: { orderIndex: { order: SortOrder.ASC } },
                    readField,
                  }),
                }
              }

              return groupInCache
            }),
            ...Object.values(newGroupsById).map((group) => {
              return {
                ...group,
                items: sortProductBoardItemsReferences({
                  items: group.items,
                  sort: { orderIndex: { order: SortOrder.ASC } },
                  readField,
                }),
              }
            }),
          ],
          details
        )

        const limit = prev.limit || ROADMAP_ITEMS_GROUPS_LIMIT

        const newDocs = sortedGroups.slice(0, prev.page * limit)

        return {
          ...prev,
          docs: newDocs,
          page: Math.floor(newDocs.length / limit) || 1,
          totalPages: sortedGroups.length > prev.totalPages * limit ? prev.totalPages + 1 : prev.totalPages,
        }
      }),
      paginatedProductBoardItems: wrapQueryModifier<
        Omit<PaginatedProductBoardItemsObj, 'docs'> & { docs: Reference[] },
        PaginatedProductBoardItemsQueryVariables
      >((prev, details) => {
        // const { variables, readField, toReference } = details
        const { variables, toReference } = details

        const limit = prev.limit || defaultPaginatedRoadmapItemsPagination.limit

        const filteredItems = filterProductBoardItems({ items: newItems, filter: variables.filter })

        if (filteredItems.length === 0) {
          return prev
        }

        // const newDocs = sortProductBoardItemsReferences({
        //   items: [...filteredItems.map((updatedItem) => toReference(updatedItem)), ...prev.docs],
        //   sort: variables.sort,
        //   readField,
        // }).slice(0, prev.page * limit)
        const newDocs = [...filteredItems.map((updatedItem) => toReference(updatedItem)), ...prev.docs].slice(
          0,
          prev.page * limit
        )

        return {
          ...prev,
          docs: newDocs,
          page: Math.floor(newDocs.length / limit) || 1,
          totalPages: newDocs.length > prev.totalPages * limit ? prev.totalPages + 1 : prev.totalPages,
        }
      }),
    },
  })
}

export function modifyRoadmapItemsGroupsAfterRoadmapCreation(
  cache: ApolloCache<any>,
  roadmap: ProductBoardRoadmapWithItemsFragment
) {
  return cache.modify({
    fields: {
      productBoardItemsGroups: wrapQueryModifier<
        { __typename?: 'PaginatedRoadmapItemsGroupsObj' } & Pick<
          PaginatedRoadmapItemsGroupsObj,
          'limit' | 'page' | 'totalPages'
        > & {
            docs: Array<
              { __typename?: 'ProductBoardItemsGroupObj' } & Pick<ProductBoardItemsGroupObj, 'id'> &
                Omit<ProductBoardItemsGroupDetailsFragment, 'items'> & { items: Reference[] }
            >
          },
        ProductBoardItemsGroupsQueryVariables
      >((prev, details) => {
        const { variables } = details
        if (variables.groupBy !== ProductBoardItemsGroupBy.ROADMAP) return prev

        const limit = prev.limit || ROADMAP_ITEMS_GROUPS_LIMIT

        // group is a obj, not a ref
        const sortedGroups = roadmapItemsGroupsOptions[ProductBoardItemsGroupBy.ROADMAP].sortGroups(
          [
            ...prev.docs,
            {
              id: roadmap.id,
              name: roadmap.name,
              key: ProductBoardItemsGroupBy.ROADMAP,
              __typename: productBoardItemsGroupTypename,
              items: [],
            },
          ],
          details
        )

        const newDocs = sortedGroups.slice(0, prev.page * limit)

        return {
          ...prev,
          docs: newDocs,
          page: Math.floor(newDocs.length / limit) || 1,
          totalPages: sortedGroups.length > prev.totalPages * limit ? prev.totalPages + 1 : prev.totalPages,
        }
      }),
    },
  })
}

export function modifyRoadmapItemsGroupsAfterRoadmapDeletion(cache: ApolloCache<any>, roadmapId: string) {
  return cache.modify({
    fields: {
      productBoardItemsGroups: wrapQueryModifier<
        { __typename?: 'PaginatedRoadmapItemsGroupsObj' } & Pick<
          PaginatedRoadmapItemsGroupsObj,
          'limit' | 'page' | 'totalPages'
        > & {
            docs: Array<
              { __typename?: 'ProductBoardItemsGroupObj' } & Pick<ProductBoardItemsGroupObj, 'id'> &
                Omit<ProductBoardItemsGroupDetailsFragment, 'items'> & { items: Reference[] }
            >
          },
        ProductBoardItemsGroupsQueryVariables
      >((prev, details) => {
        const { variables } = details
        if (variables.groupBy !== ProductBoardItemsGroupBy.ROADMAP) return prev

        const limit = prev.limit || ROADMAP_ITEMS_GROUPS_LIMIT

        const sortedGroups = roadmapItemsGroupsOptions[ProductBoardItemsGroupBy.ROADMAP].sortGroups(
          [...prev.docs.filter((doc) => doc.id !== roadmapId)],
          details
        )

        const newDocs = sortedGroups.slice(0, prev.page * limit)

        return {
          ...prev,
          docs: newDocs,
          page: Math.floor(newDocs.length / limit) || 1,
          totalPages: sortedGroups.length > prev.totalPages * limit ? prev.totalPages + 1 : prev.totalPages,
        }
      }),
    },
  })
}
